import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CopyId } from "@/components/copy-id";
import { queryKeys } from "@/lib/queryKeys";
import {
  type RecommendedUsersParams,
  usersProvider,
} from "@/providers/users.provider";
import {
  IconChevronLeft,
  IconChevronRight,
  IconSparkles,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

const PRESET_LOCATIONS = [
  { label: "São Paulo, SP", lat: -23.5505, lng: -46.6333 },
  { label: "São Carlos, SP", lat: -22.0087, lng: -47.8909 },
  { label: "Brasília, DF", lat: -15.8267, lng: -47.9218 },
];

// Raw score multipliers, not a normalized mix — each one is independent.
const DEFAULT_WEIGHTS = {
  w_in_cell: 1000,
  w_xp: 1,
  w_has_profile_image: 50,
  w_has_vehicle: 50,
  w_followers: 100,
};

type WeightKey = keyof typeof DEFAULT_WEIGHTS;

const WEIGHT_FIELDS: {
  key: WeightKey;
  label: string;
  desc: string;
  max: number;
  step: number;
}[] = [
  {
    key: "w_in_cell",
    label: "In cell",
    desc: "Flat bonus for users in the caller's own H3 cell. Neighbour-cell users get none, so this is what keeps nearby users above distant high-XP ones.",
    max: 5000,
    step: 10,
  },
  {
    key: "w_xp",
    label: "XP",
    desc: "Multiplied by the user's XP. Unbounded, so a high value lets veterans outrank the in-cell bonus.",
    max: 10,
    step: 0.1,
  },
  {
    key: "w_has_profile_image",
    label: "Profile image",
    desc: "Flat bonus for users who have uploaded a profile image.",
    max: 500,
    step: 5,
  },
  {
    key: "w_has_vehicle",
    label: "Vehicle",
    desc: "Flat bonus for users who own at least one vehicle.",
    max: 500,
    step: 5,
  },
  {
    key: "w_followers",
    label: "Followers",
    desc: "Multiplied by the user's follower count. Unbounded like XP, so popular users can outrank the in-cell bonus.",
    max: 1000,
    step: 10,
  },
];

function WeightSlider({
  label,
  name,
  value,
  max,
  step,
  onChange,
}: {
  label: string;
  name: string;
  value: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);

  function commit(raw: string) {
    const parsed = parseFloat(raw);
    if (!isNaN(parsed) && parsed >= 0) onChange(parsed);
    setDraft(null);
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs">{label}</Label>
        <input
          type="text"
          inputMode="decimal"
          value={draft ?? String(value)}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) =>
            e.key === "Enter" && commit((e.target as HTMLInputElement).value)
          }
          className="h-9 w-20 sm:h-7 font-mono text-xs tabular-nums text-right bg-muted rounded px-1.5 border border-border focus:outline-none focus:border-primary"
        />
      </div>
      <input
        type="range"
        name={name}
        min={0}
        max={max}
        step={step}
        value={Math.min(value, max)}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-primary"
      />
    </div>
  );
}

export function UserRecommendationsTab() {
  const [weightsOn, setWeightsOn] = useState(false);
  const [weights, setWeights] = useState({ ...DEFAULT_WEIGHTS });
  const [locationPreset, setLocationPreset] = useState<string>(
    PRESET_LOCATIONS[0].label,
  );
  const [customLat, setCustomLat] = useState(String(PRESET_LOCATIONS[0].lat));
  const [customLng, setCustomLng] = useState(String(PRESET_LOCATIONS[0].lng));
  const [committed, setCommitted] = useState<RecommendedUsersParams>({
    page: 1,
    lat: PRESET_LOCATIONS[0].lat,
    lng: PRESET_LOCATIONS[0].lng,
  });

  const resolvedLat = parseFloat(customLat);
  const resolvedLng = parseFloat(customLng);
  const hasValidLocation =
    resolvedLat !== undefined &&
    resolvedLng !== undefined &&
    !isNaN(resolvedLat) &&
    !isNaN(resolvedLng);

  const page = committed.page ?? 1;

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: queryKeys.usersRecommended(committed),
    queryFn: async () => {
      const t0 = performance.now();
      const result = await usersProvider.getRecommended(committed);
      return { result, ms: Math.round(performance.now() - t0) };
    },
  });

  const users = data?.result.data;
  const hasNext = data?.result.has_next ?? false;

  function handleFetch() {
    const next: RecommendedUsersParams = {
      page: 1,
      ...(hasValidLocation ? { lat: resolvedLat, lng: resolvedLng } : {}),
      ...(weightsOn ? weights : {}),
    };
    if (JSON.stringify(next) === JSON.stringify(committed)) refetch();
    else setCommitted(next);
  }

  function goToPage(p: number) {
    setCommitted((c) => ({ ...c, page: p }));
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Controls */}
      <div className="rounded-lg border p-4 flex flex-col gap-5">
        {/* Location */}
        <div className="flex flex-col gap-3">
          <p className="text-sm font-medium">Location</p>
          <div className="flex items-center gap-2 flex-wrap">
            <Select
              value={locationPreset}
              onValueChange={(v) => {
                setLocationPreset(v);
                const preset = PRESET_LOCATIONS.find((p) => p.label === v);
                if (preset) {
                  setCustomLat(String(preset.lat));
                  setCustomLng(String(preset.lng));
                }
              }}
            >
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRESET_LOCATIONS.map((p) => (
                  <SelectItem key={p.label} value={p.label}>
                    {p.label}
                  </SelectItem>
                ))}
                <SelectItem value="custom">Custom…</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="Lat (e.g. -23.5505)"
              inputMode="decimal"
              value={customLat}
              onChange={(e) => {
                setCustomLat(e.target.value);
                setLocationPreset("custom");
              }}
              className="w-full sm:w-44 font-mono text-sm"
            />
            <Input
              placeholder="Lng (e.g. -46.6333)"
              inputMode="decimal"
              value={customLng}
              onChange={(e) => {
                setCustomLng(e.target.value);
                setLocationPreset("custom");
              }}
              className="w-full sm:w-44 font-mono text-sm"
            />
          </div>
          {!hasValidLocation && (
            <p className="text-xs text-muted-foreground">
              Enter a valid lat/lng, otherwise the backend estimates the
              location from the request timezone.
            </p>
          )}
        </div>

        {/* Weights */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="user-weights-toggle" className="text-sm font-medium">
              Override weights
            </Label>
            <Switch
              id="user-weights-toggle"
              checked={weightsOn}
              onCheckedChange={setWeightsOn}
            />
            {weightsOn && (
              <button
                type="button"
                onClick={() => setWeights({ ...DEFAULT_WEIGHTS })}
                className="ml-auto text-xs text-muted-foreground hover:text-foreground"
              >
                Reset to defaults
              </button>
            )}
          </div>
          {weightsOn && (
            <div className="flex flex-col gap-4">
              <p className="text-xs text-muted-foreground">
                score = in_cell × (in own cell) + xp × XP + followers ×
                (follower count) + profile_image × (has image) + vehicle × (has
                vehicle). Per-request values win over the published
                recommendations config.
              </p>
              <div className="grid grid-cols-1 gap-y-5">
                {WEIGHT_FIELDS.map(({ key, label, desc, max, step }) => (
                  <div key={key} className="flex flex-col gap-1">
                    <p className="text-xs text-muted-foreground">{desc}</p>
                    <WeightSlider
                      label={`${label} (${key})`}
                      name={key}
                      value={weights[key]}
                      max={max}
                      step={step}
                      onChange={(v) => setWeights((w) => ({ ...w, [key]: v }))}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Button
            onClick={handleFetch}
            disabled={isFetching}
            className="gap-1.5 h-9 w-full sm:w-auto"
          >
            <IconSparkles className="size-4" />
            Fetch recommendations
          </Button>
          {data && !isFetching && (
            <span className="text-xs text-muted-foreground tabular-nums">
              {data.ms} ms
            </span>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>User ID</TableHead>
              <TableHead>Username</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="text-right">Score</TableHead>
              <TableHead className="text-right">XP</TableHead>
              <TableHead className="text-right">Distance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 7 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : isError ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-12 text-center text-muted-foreground"
                >
                  Failed to load recommendations.{" "}
                  <button
                    type="button"
                    onClick={() => refetch()}
                    className="underline hover:text-foreground"
                  >
                    Retry
                  </button>
                </TableCell>
              </TableRow>
            ) : !users?.length ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-12 text-center text-muted-foreground"
                >
                  No recommendations returned.
                </TableCell>
              </TableRow>
            ) : (
              users.map((user, i) => {
                const fullName =
                  [user.first_name, user.last_name].filter(Boolean).join(" ") ||
                  "—";
                const pageSize = data?.result.page_size ?? users.length;
                return (
                  <TableRow key={user.user_id}>
                    <TableCell className="text-xs text-muted-foreground tabular-nums">
                      {(page - 1) * pageSize + i + 1}
                    </TableCell>
                    <TableCell>
                      <CopyId id={user.user_id} />
                    </TableCell>
                    <TableCell className="font-medium max-w-[160px] truncate">
                      {user.username}
                    </TableCell>
                    <TableCell className="max-w-[160px] truncate">
                      {fullName}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm font-medium">
                      {user.score.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm">
                      {user.xp.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs text-muted-foreground">
                      {user.distance_km} km
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {users && (page > 1 || hasNext) && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => goToPage(page - 1)}
            className="gap-1 h-9 sm:h-8"
          >
            <IconChevronLeft className="size-3.5" />
            Prev
          </Button>
          <span className="text-xs text-muted-foreground">Page {page}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasNext}
            onClick={() => goToPage(page + 1)}
            className="gap-1 h-9 sm:h-8"
          >
            Next
            <IconChevronRight className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
